import React from 'react'

import { css, Theme, useTheme } from '@emotion/react'
import styled from '@emotion/styled'

import { AvatarIcon, iconIdMapping } from '@arcade/web/icons/react'

import arcadeColors, { ArcadeColors, useGetArcadeColor } from '../../theme/arcadeColors'
import { Flex } from '../layout/Flex'
import { CashDesignSystemContext } from '../Provider'
import Text from '../Text/Text'
import { StackedAvatar } from './ArcadeStackedAvatar'

export const DISABLED_OPACITY = '40%'
export const DEFAULT_CARD_SCALE_FACTOR = 1.75

interface AvatarImageObject {
  lightUrl: string
  darkUrl?: string
}

type AvatarImage = AvatarImageObject | string

/**
 * Prefer arcadeId over element.
 * Element is only used for custom icons.
 */
interface Icon {
  arcadeId?: string
  element?: React.ReactNode
}

interface CustomColorObject {
  light: string
  dark?: string
}

type ColorValue = CustomColorObject | ArcadeColors

export type StackedAvatarLayout = 'automatic' | 'horizontal'

export interface AvatarProps {
  backgroundColor?: ColorValue
  initial?: string
  alt?: string
  image?: AvatarImage
  icon?: Icon
  overlayImage?: AvatarImage
  overlayIcon?: Icon
  tintColor?: ColorValue
  borderColor?: ColorValue
  borderWidth?: string
  size?: Size
  stackedAvatars?: AvatarProps[]
  stackedAvatarLayout?: StackedAvatarLayout
  disabled?: boolean
  colorizeImage?: boolean
  /**
   * Scale factor for the primary avatar.
   * This is useful for when the image is a gift card and you want to scale it to a larger size.
   * This is not used for the overlay image.
   */
  scaleFactor?: number
}

type AvatarWrapperProps = {
  size: Size
  background?: string
  borderColor?: string
  tintColor?: string
  borderWidth?: string
}

export type NamedSize = 'xsmall' | 'small' | 'medium' | 'large'

/**
 * Avatar size. Either a named preset or a pixel width for custom sizes
 * (matches the iOS `ArcadeAvatarSize.custom(Int)` API).
 */
export type Size = NamedSize | number

const transparent = {
  light: 'transparent',
  dark: 'transparent',
}

const DEFAULT_BACKGROUND_COLORS = {
  icon: arcadeColors.backgroundBrand,
  monogram: arcadeColors.backgroundProminent,
  image: transparent,
  default: arcadeColors.backgroundStandard,
}

export type AvatarSizeDimensions = {
  rem: `${number}rem`
  width: number
  fontSize: `${number}rem`
  overlayOffset: `${number}rem`
}

/**
 * Used for caluclating the size of the avatar.
 * rem is the size of the avatar in rems
 * width is the size of the avatar in pixels -- only use this for calculating. do no use for styling
 * fontSize is the size of the font in rems -- used for monogram avatars
 * overlayOffset is the offset of the overlay in rems -- used for overlay avatars
 */
export const avatarSizeDimensions: { [key in NamedSize]: AvatarSizeDimensions } = {
  xsmall: { rem: '2rem', width: 32, fontSize: '1rem', overlayOffset: '0.5rem' },
  small: { rem: '3rem', width: 48, fontSize: '1.5rem', overlayOffset: '0.5rem' },
  medium: { rem: '4rem', width: 64, fontSize: '2rem', overlayOffset: '0.25rem' },
  large: { rem: '6rem', width: 96, fontSize: '3rem', overlayOffset: '0rem' },
}

export const getAvatarSizeDimensions = (size: Size): AvatarSizeDimensions => {
  if (typeof size === 'string') {
    return avatarSizeDimensions[size]
  }
  // overlayOffset is linearly interpolated from `small` (48 → 0.5rem) and
  // `medium` (64 → 0.25rem), clamping to 0 at width ≥ 80px.
  return {
    rem: `${size / 16}rem`,
    width: size,
    fontSize: `${size / 32}rem`,
    overlayOffset: `${Math.max(0, (80 - size) / 64)}rem`,
  }
}

const AvatarWrapper = styled.div<{ size: Size; disabled?: boolean }>`
  position: relative;
  width: fit-content;
  ${({ disabled }) => disabled && `opacity: ${DISABLED_OPACITY};`};

  ${({ size }) => {
    const avatarSize = getAvatarSizeDimensions(size).rem
    return css`
      height: ${avatarSize};
      width: ${avatarSize};
    `
  }}
`

export const PrimaryAvatarWrapper = styled(Flex)<AvatarWrapperProps>`
  overflow: hidden;
  border-radius: ${({ theme }) => theme.border.radius.circle};
  font-weight: ${({ theme }) => theme.weights.medium}; /* stylelint-disable-line @cash-web/no-hardcoded-font-styles */
  border: ${({ theme, borderColor, borderWidth }) =>
    borderColor &&
    `${borderWidth ?? theme.border.width.small} solid ${useGetArcadeColor(borderColor as ArcadeColors) ?? borderColor};`};
  ${({ size, background = 'backgroundStandard', tintColor = 'iconInverse' }) => {
    return css`
      height: ${getAvatarSizeDimensions(size).rem};
      width: ${getAvatarSizeDimensions(size).rem};
      background-color: ${useGetArcadeColor(background as ArcadeColors) ?? background};
      color: ${useGetArcadeColor(tintColor as ArcadeColors) ?? tintColor};
    `
  }}

  user-select: none;
`

const getMonogramFontSize = ({ size, text }: { size: Size; text: string }) => {
  const avatarWidth = getAvatarSizeDimensions(size).width
  if (text.length <= 2) {
    return getAvatarSizeDimensions(size).fontSize
  }

  const fontSize = (avatarWidth / ((text.length - Math.max(text.length - 4, 0) / 2) * 16)) * (35.785 / 35)
  return `${Number(fontSize.toFixed(4))}rem`
}

const MonogramAvatarWrapper = styled.div<{ size: Size; text: string }>`
  /* stylelint-disable-next-line @cash-web/no-hardcoded-font-styles */
  font-size: ${({ size, text }) => getMonogramFontSize({ size, text })};
  max-width: 100%;
  overflow: hidden;
  text-align: center;
  white-space: nowrap;
`
const ScaleWrapper = styled.div<{ scaleFactor: number }>`
  transform: scale(${({ scaleFactor }) => scaleFactor});
  display: flex;
  align-items: center;
  justify-content: center;
`

const OverlayAvatarWrapper = styled(Flex)<{ size: Size; hasPadding: boolean }>`
  position: absolute;
  width: 1.5rem;
  height: 1.5rem;
  padding: ${({ hasPadding, theme }) => (hasPadding ? theme.pxToRem(4) : '0')};
  border-radius: ${({ theme }) => theme.border.radius.circle};
  background-color: ${({ theme }) => theme.arcadeColors.semantic.backgroundSubtle};
  color: ${({ theme }) => theme.arcadeColors.semantic.iconStandard};
  border: ${({ theme }) => `0.125rem solid ${theme.arcadeColors.semantic.borderApp};`};
  overflow: hidden;
  align-items: center;
  justify-content: center;

  bottom: -${({ size }) => getAvatarSizeDimensions(size).overlayOffset};
  right: -${({ size }) => getAvatarSizeDimensions(size).overlayOffset};
`

const UrlImage = styled.img<{
  colorizeImage?: boolean
}>`
  width: 100%;

  ${({ colorizeImage }) => {
    if (colorizeImage) {
      return css`
        filter: brightness(0) invert(1);
      `
    }
    return ''
  }}
`

const getColorForTheme = ({ color, isDarkMode }: { color?: ColorValue; isDarkMode: boolean }) => {
  if (typeof color === 'string') {
    return color
  }
  // If dark mode is enabled and a dark color is provided, use the dark color, otherwise use the light color
  return isDarkMode && color?.dark ? color?.dark : color?.light
}

const UrlImageAvatar = ({
  image,
  colorizeImage,
  size,
  theme,
  alt,
  scaleFactor,
}: {
  image: AvatarImage
  colorizeImage?: boolean
  size: Size
  theme: Theme
  alt: string
  scaleFactor: number
}) => {
  // Handle both string and object image types
  let imageUrl: string
  if (typeof image === 'string') {
    imageUrl = image
  } else {
    imageUrl = theme.darkMode && image.darkUrl ? image.darkUrl : image.lightUrl
  }
  const { imageUrlHandler } = React.useContext(CashDesignSystemContext)

  if (!imageUrlHandler) {
    throw new Error('imageUrlHandler is not provided but required in Avatar component')
  }

  const width =
    getAvatarSizeDimensions(size).width <= avatarSizeDimensions.medium.width
      ? avatarSizeDimensions.medium.width
      : getAvatarSizeDimensions(size).width

  const scaledWidth = width * scaleFactor

  if (imageUrl) {
    const img = imageUrlHandler({ url: imageUrl, width: scaledWidth })

    return <UrlImage alt={alt} colorizeImage={colorizeImage} src={img} />
  }

  return null
}

const MonogramAvatar = ({ monogramInitial, size }: { monogramInitial: string; size: Size }) => {
  if (!monogramInitial.length) {
    return null
  }

  return (
    <MonogramAvatarWrapper size={size} text={monogramInitial}>
      <Text color="textInverse">{monogramInitial}</Text>
    </MonogramAvatarWrapper>
  )
}

const getArcadeIconComponent = (icon: Icon) => {
  const arcadeIcon = icon?.arcadeId ? iconIdMapping[icon?.arcadeId] : undefined
  if (!arcadeIcon) {
    throw Error(`Avatar: No icon mapping found for id ${arcadeIcon}`)
  }
  return <arcadeIcon.Icon size={arcadeIcon.size} />
}

const getPrimaryAvatarInfo = ({
  image,
  icon,
  initial,
  size,
  alt,
  theme,
  scaleFactor,
  colorizeImage,
}: {
  image?: AvatarImage
  icon?: Icon
  initial?: string
  size: Size
  alt: string
  theme: Theme
  scaleFactor: number
  colorizeImage?: boolean
}): {
  primaryAvatar: React.ReactNode
  defaultBackgroundColor: keyof typeof DEFAULT_BACKGROUND_COLORS
} => {
  if (image && (typeof image === 'string' || image.lightUrl)) {
    return {
      primaryAvatar: (
        <UrlImageAvatar
          image={image}
          size={size}
          theme={theme}
          alt={alt}
          colorizeImage={colorizeImage}
          scaleFactor={scaleFactor}
        />
      ),
      defaultBackgroundColor: 'image',
    }
  }
  if (initial) {
    return {
      primaryAvatar: <MonogramAvatar monogramInitial={initial} size={size} />,
      defaultBackgroundColor: 'monogram',
    }
  }
  if (icon?.arcadeId) {
    return {
      primaryAvatar: getArcadeIconComponent(icon),
      defaultBackgroundColor: 'icon',
    }
  }
  if (icon?.element) {
    return {
      primaryAvatar: icon.element,
      defaultBackgroundColor: 'icon',
    }
  }

  // If no primary avatar is found, default to the avatar icon
  return {
    primaryAvatar: <AvatarIcon size={24} data-testid="default-avatar" />,
    defaultBackgroundColor: 'default',
  }
}

const getOverlayAvatar = ({
  overlayImage,
  overlayIcon,

  size,
  alt,
  theme,
}: {
  overlayImage?: AvatarImage
  overlayIcon?: Icon
  size: Size
  alt: string
  theme: Theme
}) => {
  const avatarWidth = getAvatarSizeDimensions(size).width
  const isBelowXSmall = avatarWidth < avatarSizeDimensions.xsmall.width

  if (isBelowXSmall || (!overlayImage && !overlayIcon)) {
    return null
  }
  if (overlayIcon?.arcadeId) {
    return getArcadeIconComponent(overlayIcon)
  } else if (overlayIcon?.element) {
    return overlayIcon.element
  } else if (overlayImage) {
    return <UrlImageAvatar image={overlayImage} size={size} theme={theme} alt={alt} scaleFactor={1} />
  } else {
    return null
  }
}

/**
 * Arcade Avatar component
 *
 * If stackedAvatars are provided, they will be rendered over the other avatar props.
 * If no primary avatar is found, the default profile avatar icon will be rendered.
 *
 * Priority order for primary avatar:
 * 1. Image
 * 2. Monogram
 * 3. Icon
 *
 * Priority order for overlay if overlay props are provided:
 *  1. Icon
 *  2. Image
 */
export const Avatar = ({
  backgroundColor,
  initial,
  alt = 'avatar image',
  image,
  icon,
  overlayImage,
  overlayIcon,
  tintColor,
  borderColor,
  borderWidth,
  size = 'medium',
  stackedAvatars,
  stackedAvatarLayout,
  disabled,
  colorizeImage,
  scaleFactor = 1,
}: AvatarProps) => {
  const theme = useTheme()

  if (stackedAvatars) {
    return (
      <StackedAvatar stackedAvatars={stackedAvatars} size={size} disabled={disabled} layout={stackedAvatarLayout} />
    )
  }

  const { primaryAvatar, defaultBackgroundColor } = getPrimaryAvatarInfo({
    image,
    icon,
    initial,
    size,
    alt,
    colorizeImage,
    theme,
    scaleFactor,
  })
  const overlayAvatar = getOverlayAvatar({
    overlayImage,
    overlayIcon,
    size,
    alt,
    theme,
  })

  const avatarBackgroundColor = getColorForTheme({ color: backgroundColor, isDarkMode: theme.darkMode })
  const avatarBorderColor = getColorForTheme({ color: borderColor, isDarkMode: theme.darkMode })
  const avatarTintColor = getColorForTheme({ color: tintColor, isDarkMode: theme.darkMode })
  const defaultBackground = getColorForTheme({
    color: DEFAULT_BACKGROUND_COLORS[defaultBackgroundColor],
    isDarkMode: theme.darkMode,
  })

  return (
    <AvatarWrapper size={size} disabled={disabled} data-testid="avatar-wrapper">
      <PrimaryAvatarWrapper
        background={avatarBackgroundColor ?? defaultBackground}
        borderColor={avatarBorderColor}
        borderWidth={borderWidth}
        tintColor={avatarTintColor}
        size={size}
        justifyContent="center"
        alignItems="center"
        aria-label={alt}
      >
        <ScaleWrapper scaleFactor={scaleFactor}>{primaryAvatar}</ScaleWrapper>
      </PrimaryAvatarWrapper>

      {overlayAvatar && (
        <OverlayAvatarWrapper size={size} data-testid="overlay-avatar" hasPadding={!!overlayIcon?.arcadeId}>
          {overlayAvatar}
        </OverlayAvatarWrapper>
      )}
    </AvatarWrapper>
  )
}
